import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createMaintenanceWindow,
  disableMaintenanceWindow,
  fetchMaintenanceWindowDetail,
  fetchMaintenanceWindows,
  updateMaintenanceWindow,
} from "@/lib/maintenance-windows-api";
import { queryKeys } from "@/lib/query-keys";
import type {
  MaintenanceWindowFilters,
  MaintenanceWindowPayload,
} from "@/types/maintenance-windows";

export function useMaintenanceWindows(filters: MaintenanceWindowFilters) {
  return useQuery({
    queryKey: queryKeys.maintenanceWindows.list(filters),
    queryFn: () => fetchMaintenanceWindows(filters),
    staleTime: 60 * 1000,
  });
}

export function useMaintenanceWindowDetail(
  windowId?: number,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.maintenanceWindows.detail(windowId ?? 0),
    queryFn: () => fetchMaintenanceWindowDetail(windowId ?? 0),
    enabled: enabled && Boolean(windowId),
    staleTime: 60 * 1000,
  });
}

export function useCreateMaintenanceWindow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: MaintenanceWindowPayload) =>
      createMaintenanceWindow(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.maintenanceWindows.all,
      });
    },
  });
}

export function useUpdateMaintenanceWindow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      windowId,
      payload,
    }: {
      windowId: number;
      payload: MaintenanceWindowPayload;
    }) => updateMaintenanceWindow(windowId, payload),
    onSuccess: (_, { windowId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.maintenanceWindows.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.maintenanceWindows.detail(windowId),
      });
    },
  });
}

export function useDisableMaintenanceWindow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (windowId: number) => disableMaintenanceWindow(windowId),
    onSuccess: (_, windowId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.maintenanceWindows.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.maintenanceWindows.detail(windowId),
      });
    },
  });
}
